import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  input,
  Output,
  signal,
  Signal,
  viewChild,
} from '@angular/core';

import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonTheme, IconPosition, NotificationScale, NotificationTheme } from '@fcom/ui-components';
import { findFirstFocusableElement } from '@fcom/ui-components/utils/dom.utils';
import { ServiceCatalogService } from '@fcom/dapi/interfaces/services.interface';
import { Category } from '@fcom/dapi/api/models/category';
import { FinnairPassengerItem } from '@fcom/dapi/api/models/finnair-passenger-item';

import { overlappingSelectionsForCategorySelector } from '../../../../../store/selectors/services.selector';
import { getServiceOverlapStateForPaxForFlight } from '../../../utils/ancillary.utils';
import { ConstituentPair, ServiceOverlapResolution, ServiceOverlaps } from '../../../interfaces/ancillaries-interface';

interface ButtonContent {
  textKey: string;
  icon: { name: SvgLibraryIcon; position: IconPosition };
}

const ADD_CONTENT: ButtonContent = {
  textKey: 'ancillaries.bundle.upsellCard.buttonText.addCombo',
  icon: { name: SvgLibraryIcon.PLUS, position: IconPosition.LEFT },
};

const REMOVE_CONTENT: ButtonContent = {
  textKey: 'ancillaries.bundle.toggle.buttonText.removeCombo',
  icon: { name: SvgLibraryIcon.TRASH, position: IconPosition.LEFT },
};

/**
 * One passenger's offer for an ancillary+ancillary (non-seat) bundle: the passenger name and
 * "Add combo" action, the bundle's constituent service cards and the passenger-specific price. When the
 * bundle isn't available for this passenger on this flight (`bundleItem` null), the
 * passenger is still listed but the offer is replaced by a short "not available" notice.
 */
@Component({
  selector: 'fin-bundle-offer-card',
  templateUrl: './bundle-offer-card.component.html',
  styleUrls: ['./bundle-offer-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BundleOfferCardComponent {
  private store$ = inject(Store);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private injector = inject(Injector);

  readonly constituents = input.required<ConstituentPair>();
  readonly passenger = input.required<FinnairPassengerItem>();
  readonly bundleItem = input.required<ServiceCatalogService | null>();
  readonly selectedForPassenger = input.required<boolean>();
  readonly category = input.required<Category>();
  readonly fragmentId = input.required<string>();

  @Output() addBundle = new EventEmitter<void>();
  @Output() removeBundle = new EventEmitter<void>();

  private passengerHeading = viewChild.required<ElementRef<HTMLElement>>('passengerHeading');
  private overlapResolution = signal<ServiceOverlapResolution | undefined>(undefined);

  private readonly serviceOverlapsForCategory = computed(() =>
    this.store$.selectSignal(overlappingSelectionsForCategorySelector(this.category()))()
  );

  readonly serviceOverlaps: Signal<ServiceOverlaps> = computed(() => {
    const resolution = this.overlapResolution();

    return getServiceOverlapStateForPaxForFlight(
      this.serviceOverlapsForCategory(),
      this.fragmentId(),
      this.passenger().id,
      resolution
    );
  });

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonTheme = ButtonTheme;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationScale = NotificationScale;

  readonly passengerName = computed(() =>
    [this.passenger()?.firstName, this.passenger()?.lastName].filter(Boolean).join(' ')
  );
  readonly buttonConfig = computed(() => {
    const selected = this.selectedForPassenger();
    const canAdd = !!this.bundleItem() && !this.serviceOverlaps().blockSelections;
    return { ...(selected ? REMOVE_CONTENT : ADD_CONTENT), disabled: !selected && !canAdd };
  });

  toggleBundle(): void {
    (this.selectedForPassenger() ? this.removeBundle : this.addBundle).emit();
  }

  continueToSelection(): void {
    this.overlapResolution.set('discard');
    this.addBundle.emit();
    afterNextRender(() => findFirstFocusableElement(this.elementRef.nativeElement)?.focus(), {
      injector: this.injector,
    });
  }

  keepExistingSelection(): void {
    this.overlapResolution.set('keep');
    afterNextRender(() => this.passengerHeading().nativeElement.focus(), { injector: this.injector });
  }
}
